// frontend/src/pages/Admin.jsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { formatDateTime } from "../utils/helperFunction";
import { toast } from "react-toastify";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { updatePageTitle } from "../utils/titleUtils";

// API Services
import {
  getAllOrganizations,
  updateOrganization as updateOrg,
} from "../api/apiFunction/orgServices";
import { getEmployees } from "../api/apiFunction/employeesServices";
import { getAllPlan } from "../api/apiFunction/planServices";

// Components
import Sidebar from "../components/pages/admin/Sidebar";
import Header from "../components/pages/admin/Header";
import { OrganizationsTabs } from "../components/pages/admin/organizations";

// Import AdminDashboard component
import AdminDashboard from "../components/pages/admin/AdminDashboard";
import IndividualsTab from "../components/pages/admin/individuals/IndividualsTab";
import PlanSettingsTab from "../components/pages/admin/PlanSettingsTab";
import ModelsPage from "../components/pages/admin/models";

// Usage tab
import { OverallUsage } from "../components/pages/admin/usage";
import IndividualsUsageTab from "../components/pages/admin/usage/IndividualsUsageTab";
import OrganizationsUsageTab from "../components/pages/admin/usage/OrganizationsUsageTab";

// Partner components
import { PartnerApplication } from "../components/pages/admin/partners";

// Personas components
import PersonasTab from "../components/pages/admin/personas/PersonasTab";

// Newsletter components
import { NewsletterTab } from "../components/pages/admin/newsletter";

// Sentry Dashboard
import SentryDashboard from "../components/pages/admin/SentryDashboard";

// Role Management
import RoleManagement from "../components/pages/admin/RoleManagement";
import ProtectedAdminRoute from "../components/ProtectedAdminRoute";
import UserDebugPanel from "../components/pages/admin/UserDebugPanel";

// Dashboard component
const Dashboard = ({ organizations }) => (
  <div className="h-full p-6">
    <div className="max-w-7xl mx-auto">
      <AdminDashboard organizations={organizations} />
    </div>
  </div>
);

const Admin = () => {
  const { theme, toggleTheme } = useTheme();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);
  const [sortOption, setSortOption] = useState("name-asc"); // name-asc, name-desc, date-asc, date-desc
  const [organizations, setOrganizations] = useState([]);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: "",
    domain: "",
  });
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("all");
  const location = useLocation();
  let headerTitle = "";
  if (location.pathname === "/" || location.pathname === "/admin")
    headerTitle = "Dashboard";
  else if (location.pathname.startsWith("/admin/organizations"))
    headerTitle = "Organizations";
  else if (location.pathname.startsWith("/admin/requests"))
    headerTitle = "Individuals";
  else if (location.pathname.startsWith("/admin/personas"))
    headerTitle = "Personas";
  else if (location.pathname.startsWith("/admin/newsletter"))
    headerTitle = "Newsletter & Email";
  else if (location.pathname.startsWith("/admin/plans"))
    headerTitle = "Plan Settings";
  else if (location.pathname.startsWith("/admin/usage")) headerTitle = "Usage";
  else if (location.pathname.startsWith("/admin/models")) headerTitle = "Models";
  else if (location.pathname.startsWith("/admin/partners")) headerTitle = "Partners";
  else if (location.pathname.startsWith("/admin/sentry-dashboard")) headerTitle = "App Monitoring";
  else if (location.pathname.startsWith("/admin/roles")) headerTitle = "Role & User Management";
  else headerTitle = "";

  useEffect(() => {
    fetchPlans();
    updatePageTitle(headerTitle);
    refreshUserData(); // Refresh user data to get latest adminRole
  }, []);

  // Refresh user data from server to get latest adminRole
  const refreshUserData = async () => {
    try {
      const { verifyUser } = await import("../api/apiFunction/userServices");
      const res = await verifyUser();
      if (res?.data) {
        // Get current user data from localStorage
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        // Merge with fresh data from server, preserving token
        const updatedUser = {
          ...currentUser,
          ...res.data,
          token: currentUser.token, // Preserve token
          refreshToken: currentUser.refreshToken // Preserve refresh token
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        // Trigger a storage event to update Sidebar
        window.dispatchEvent(new Event('storage'));
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
  };

  useEffect(() => {
    if (plans.length > 0) {
      getAllOrg();
    }
  }, [plans]);

  const fetchPlans = async () => {
    try {
      const res = await getAllPlan();
      if (Array.isArray(res?.data)) {
        setPlans(res.data);
      } else {
        setPlans([]);
      }
    } catch (error) {
      setPlans([]);
    }
  };

  const getAllOrg = async () => {
    try {
      setIsLoadingOrgs(true);
      const response = await getAllOrganizations();
      if (response?.data) {
        // Map API response to match our expected format
        const orgs = response.data.map((org) => {
          // Find plan name by id (org.plan is id, plan._id is id)
          const planObj = plans.find((p) => String(p._id) === String(org.plan));
          return {
            id: org._id,
            name: org.name,
            domain: org.domain,
            status: org.status || false,
            createdAt: formatDateTime(org.createdAt),
            updatedAt: formatDateTime(org.updatedAt),
            planName: planObj ? planObj.name : "N/A",
          };
        });
        setOrganizations(orgs);
      }
    } catch (error) {
      toast.error("Failed to fetch organizations");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const fetchEmployees = useCallback(async (orgId) => {
    try {
      setIsLoadingEmployees(true);
      const response = await getEmployees(orgId);
      setEmployees(response);
      return response;
    } catch (error) {
      toast.error("Failed to load employees");
      throw error;
    } finally {
      setIsLoadingEmployees(false);
    }
  }, []);

  const handleOrgNameClick = useCallback(
    async (org) => {
      setSelectedOrg(org);
      setShowEmployeeModal(true);
      try {
        await fetchEmployees(org.id);
      } catch {
        // Error is already handled in fetchEmployees
      }
    },
    [fetchEmployees]
  );

  const handleOrgStatusChange = useCallback(
    async (org, isChecked) => {
      try {
        const newStatus = isChecked; // true or false
        // Update local state optimistically
        const updatedOrgs = organizations.map((o) =>
          o.id === org.id ? { ...o, status: newStatus } : o
        );
        setOrganizations(updatedOrgs);

        // Call API to update organization status
        const response = await updateOrg(org.id, { status: newStatus });

        if (!response || !response.data) {
          // If API call fails, revert the local state
          const revertedOrgs = organizations.map((o) =>
            o.id === org.id ? { ...o, status: org.status } : o
          );
          setOrganizations(revertedOrgs);
          toast.error("Failed to update organization status");
        } else {
          toast.success("Organization status updated successfully");
        }
      } catch (error) {
        // Revert local state on error
        const revertedOrgs = organizations.map((o) =>
          o.id === org.id ? { ...o, status: org.status } : o
        );
        setOrganizations(revertedOrgs);
        toast.error("Failed to update organization status");
      }
    },
    [organizations]
  );

  // Filter and sort organizations
  const filteredOrganizations = useMemo(() => {
    return organizations
      .filter(
        (org) =>
          org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          org.domain.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        switch (sortOption) {
          case "name-asc":
            return a.name.localeCompare(b.name);
          case "name-desc":
            return b.name.localeCompare(a.name);
          case "date-asc":
            return new Date(a.createdAt) - new Date(b.createdAt);
          case "date-desc":
            return new Date(b.createdAt) - new Date(a.createdAt);
          default:
            return 0;
        }
      });
  }, [organizations, searchQuery, sortOption]);

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleAddOrganization = () => {
    if (!newOrg.name || !newOrg.domain) {
      toast.error("Please fill in all fields");
      return;
    }

    // Domain validation
    const domainRegex =
      /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newOrg.domain)) {
      toast.error("Please enter a valid domain");
      return;
    }

    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      const newOrgWithId = {
        id: organizations.length + 1,
        ...newOrg,
        employeeCount: 0,
        createdAt: new Date().toISOString().split("T")[0],
      };

      setOrganizations([...organizations, newOrgWithId]);
      setNewOrg({ name: "", domain: "" });
      setShowAddOrgModal(false);
      setIsLoading(false);
      toast.success("Organization added successfully");
    }, 1000);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setTimeout(() => {
      window.location.href = "/login";
    }, 1000);
  };

  return (
    <div className="flex h-screen bg-bg-50 text-fg-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        sidebarExpanded={sidebarExpanded}
        showSearch={showSearch}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setShowSearch={setShowSearch}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header
          sidebarExpanded={sidebarExpanded}
          toggleSidebar={toggleSidebar}
          theme={theme}
          toggleTheme={toggleTheme}
          handleLogout={handleLogout}
          title={headerTitle}
        />

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedAdminRoute requiredRoute="/admin">
                  <div className="h-full overflow-y-auto">
                    <Dashboard organizations={organizations} />
                  </div>
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <ProtectedAdminRoute requiredRoute="/admin/organizations">
                  <div className="h-full overflow-y-auto">
                    <OrganizationsTabs
                      organizations={organizations}
                      isLoadingOrgs={isLoadingOrgs}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      sortOption={sortOption}
                      setSortOption={setSortOption}
                      handleOrgNameClick={handleOrgNameClick}
                      handleOrgStatusChange={handleOrgStatusChange}
                      setShowAddOrgModal={setShowAddOrgModal}
                      showAddOrgModal={showAddOrgModal}
                      newOrg={newOrg}
                      setNewOrg={setNewOrg}
                      isLoading={isLoading}
                      handleAddOrganization={handleAddOrganization}
                      showEmployeeModal={showEmployeeModal}
                      selectedOrg={selectedOrg}
                      employees={employees}
                      isLoadingEmployees={isLoadingEmployees}
                      setShowEmployeeModal={setShowEmployeeModal}
                    />
                  </div>
                </ProtectedAdminRoute>
              }
            />
            <Route 
              path="/requests" 
              element={
                <ProtectedAdminRoute requiredRoute="/admin/requests">
                  <IndividualsTab hideTitle />
                </ProtectedAdminRoute>
              } 
            />
            <Route 
              path="/personas" 
              element={
                <ProtectedAdminRoute requiredRoute="/admin/personas">
                  <PersonasTab />
                </ProtectedAdminRoute>
              } 
            />
            <Route 
              path="/newsletter" 
              element={
                <ProtectedAdminRoute requiredRoute="/admin/newsletter">
                  <NewsletterTab />
                </ProtectedAdminRoute>
              } 
            />
            <Route 
              path="/plans" 
              element={
                <ProtectedAdminRoute requiredRoute="/admin/plans">
                  <PlanSettingsTab />
                </ProtectedAdminRoute>
              } 
            />
            <Route 
              path="/models" 
              element={
                <ProtectedAdminRoute requiredRoute="/admin/models">
                  <ModelsPage />
                </ProtectedAdminRoute>
              } 
            />
            <Route 
              path="/usage" 
              element={
                <ProtectedAdminRoute requiredRoute="/admin/usage">
                  <OverallUsage />
                </ProtectedAdminRoute>
              } 
            />
            <Route
              path="/usage/individuals"
              element={
                <ProtectedAdminRoute requiredRoute="/admin/usage/individuals">
                  <IndividualsUsageTab />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/usage/organizations"
              element={
                <ProtectedAdminRoute requiredRoute="/admin/usage/organizations">
                  <OrganizationsUsageTab />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/partners"
              element={
                <ProtectedAdminRoute requiredRoute="/admin/partners">
                  <PartnerApplication />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/sentry-dashboard"
              element={
                <ProtectedAdminRoute requiredRoute="/admin/sentry-dashboard">
                  <SentryDashboard />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/roles"
              element={<RoleManagement />}
            />

          </Routes>
        </div>
      </div>
      <UserDebugPanel />
    </div>
  );
};

export default Admin;
